import { ProviderData } from "@/types/provider";
import { 
  Building2, 
  Clock, 
  Percent, 
  CheckCircle2,
  ShieldCheck,
  Wallet,
  Calendar
} from "lucide-react";
import plus1Logo from "@/assets/plus1-logga.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";

export const plus1Data: ProviderData = {
  id: "plus1",
  name: "Plus1",
  slug: "plus1",
  logo: plus1Logo,
  type: "private_loan",
  
  trustpilot: {
    rating: 4.7,
    reviews: 3851,
    url: "https://se.trustpilot.com/review/plus1.com"
  },
  
  hero: {
    badges: [
      { text: "Specialiserade på samlingslån", variant: "default" }
    ],
    description: "Plus1 erbjuder privatlån och samlingslån från 30 000 kr upp till 490 000 kr med möjlighet att låna trots betalningsanmärkningar.",
    ctaText: "Ansök hos Plus1",
    ctaButtonText: "Ansök hos Plus1",
    ctaButtonVariant: "default",
    stickyCTASubtitle: "Effektiv ränta 15,75 - 22,55%"
  },
  
  stats: [
    {
      icon: Wallet,
      value: "490 000 kr",
      label: "Maxbelopp",
      description: "Från 30 000 kr"
    },
    {
      icon: Percent,
      value: "15,75 - 22,55 %",
      label: "Effektiv ränta",
      description: "Rörlig ränta"
    },
    {
      icon: Calendar,
      value: "5 – 12 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: Building2,
      value: "588 kr",
      label: "Uppläggningsavgift",
      description: "Engångsavgift"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "UC-kontroll görs"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Kan accepteras"
    }
  ],
  
  features: {
    pros: [
      "Hjälper dig att samla lån",
      "Accepterar betalningsanmärkningar"
    ],
    cons: [
      "Relativt hög ränta på samlingslån"
    ]
  },
  
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: Clock,
      title: "Svar på ansökan",
      value: "Inom 1 dygn"
    }
  ],
  
  termsAndConditions: [
    {
      term: "Lånebelopp",
      value: "30 000 – 490 000 kr"
    },
    {
      term: "Nominell ränta",
      value: "7,00 – 22,50 %"
    },
    {
      term: "Effektiv ränta",
      value: "15,75 – 22,55 %"
    },
    {
      term: "Löptid",
      value: "5 – 12 år"
    },
    {
      term: "Uppläggningsavgift",
      value: "588 kr"
    },
    {
      term: "Lån utan UC",
      value: "Nej"
    },
    {
      term: "Svar på ansökan",
      value: "Inom 1 dygn"
    },
    {
      term: "Betalningslösning",
      value: "Banköverföring"
    }
  ],
  
  loanRequirements: [
    {
      requirement: "Legitimering",
      value: "Ja, via BankID"
    },
    {
      requirement: "Folkbokförd i Sverige",
      value: "Ja"
    },
    {
      requirement: "Svenskt personnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt mobilnummer",
      value: "Nej"
    },
    {
      requirement: "Svenskt kontonummer",
      value: "Nej"
    },
    {
      requirement: "Minsta årsinkomst",
      value: "204 000 kr"
    },
    {
      requirement: "Bet.anmärkning OK?",
      value: "Ja"
    },
    {
      requirement: "Ålderskrav",
      value: "21 +"
    },
    {
      requirement: "Anställningskrav",
      value: "Minst 1 fast anställd"
    }
  ],
  
  interestRateSection: {
    title: "Plus1s ränta & avgifter",
    description: "Avgifterna kontrollerades senast 9 december, 2025. Räntan är rörlig och sätts individuellt. För ett annuitetslån på 160 000 kr där räntan startar på 22,50 % och sänks med 0,5 procentenheter var tredje månad och en återbetalningstid på 8 år med 96 st avbetalningar om i snitt 3 063 kr och 588 kr i uppläggningsavgift ger en effektiv ränta på totalt 19,86 %. Totalt att återbetala blir 294 600 kr.",
    rateTable: [
      {
        label: "Lånebelopp (lägsta – högsta)",
        value: "30 000 – 490 000 kr"
      },
      {
        label: "Startränta (nominell)",
        value: "22,50 %"
      },
      {
        label: "Slutränta (nominell)",
        value: "7,00 %"
      },
      {
        label: "Snittränta (nominell)",
        value: "14,75 %"
      },
      {
        label: "Effektiv ränta",
        value: "15,75 – 22,55 %"
      },
      {
        label: "Uppläggningsavgift",
        value: "588 kr"
      },
      {
        label: "Aviavgift",
        value: "29 kr"
      }
    ]
  },
  
  payoutSection: {
    title: "Plus1s utbetalning",
    description: "Utbetalningstiden beror på när din låneansökan blir beviljad:",
    payoutTable: [
      {
        timeframe: "Beviljad före 10:00",
        duration: "1-2 dagar"
      },
      {
        timeframe: "Beviljad efter 10:00",
        duration: "1-3 dagar"
      }
    ]
  },
  
  faq: [
    {
      question: "Hur mycket kan man låna hos Plus1?",
      answer: "Hos Plus1 kan du ansöka om lån på mellan 30 000 och 490 000 kronor."
    },
    {
      question: "Accepterar Plus1 betalningsanmärkningar?",
      answer: "Ja, Plus1 kan acceptera betalningsanmärkningar om de bedömer att du har bra nuvarande betalningsförmåga."
    },
    {
      question: "Kan man låna utan UC hos Plus1?",
      answer: "Nej, Plus1 gör alltid en UC-kontroll vid låneansökan."
    },
    {
      question: "Hur lång tid tar det att få svar från Plus1?",
      answer: "Du får normalt svar på din ansökan inom 1 dygn."
    },
    {
      question: "Hur snabbt betalas lånet ut från Plus1?",
      answer: "Om din ansökan beviljas före kl 10:00 tar utbetalningen 1-2 dagar. Om den beviljas efter kl 10:00 tar det 1-3 dagar."
    },
    {
      question: "Vad är ålderskravet för att låna hos Plus1?",
      answer: "Du måste vara minst 21 år för att ansöka om lån hos Plus1."
    },
    {
      question: "Vad är inkomstkravet hos Plus1?",
      answer: "Du måste ha en årsinkomst på minst 204 000 kr och vara fast anställd för att kunna ansöka om lån hos Plus1."
    },
    {
      question: "Vilken ränta har Plus1?",
      answer: "Plus1 har en rörlig ränta som startar på 22,50% och sänks med 0,5 procentenheter var tredje månad ned till 7,00%. Snitträntan (nominell) är 14,75% och maximal effektiv ränta är 19,86%."
    }
  ],
  
  customerService: {
    description: "Plus1s kundtjänst är tillgänglig för att hjälpa dig med frågor om ditt lån.",
    phone: "08-41020304",
    phoneHours: ["Vardagar: 09.00-17.00", "Lördag: Stängt", "Söndag: Stängt"],
    email: "info@plus1.com"
  },
  
  companyInfo: {
    name: "0to9 AB",
    orgNumber: "556976-4110",
    address: "Eriksbergsgatan 31",
    city: "114 30 Stockholm",
    website: "https://www.plus1.com",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=138667"
  },
  
  affiliateLink: {
    linkId: "plus1-main-cta",
    trackingSource: "plus1-page",
    url: "https://go.adt291.com/t/t?a=1271269082&as=2024680921&t=2&tk=1"
  },
  
  seo: {
    title: "Plus1 Privatlån 2025 - Låna upp till 490 000 kr | Jämför & Ansök",
    description: "Plus1 erbjuder privatlån och samlingslån från 30 000 kr upp till 490 000 kr. Effektiv ränta 15,75-22,55%. Specialiserade på samlingslån. Jämför villkor och ansök idag!",
    keywords: ["plus1", "plus1 lån", "privatlån plus1", "samlingslån plus1", "lån med betalningsanmärkning", "låna pengar"]
  },
  
  schema: {
    productName: "Plus1 Privatlån",
    description: "Privatlån och samlingslån från Plus1 med möjlighet att låna trots betalningsanmärkningar",
    url: "https://www.plus1.se",
    provider: "Plus1 Finans AB",
    interestRate: {
      min: 15.75,
      max: 22.55
    },
    minAmount: 30000,
    maxAmount: 490000,
    loanTerm: ["5 år", "12 år"],
    featureList: [
      "Specialiserade på samlingslån",
      "Snabbt svar inom 1 dygn",
      "Upp till 490 000 kr",
      "Flexibla återbetalningsvillkor"
    ],
    aggregateRating: {
      ratingValue: 4.7,
      reviewCount: 3851
    }
  }
};
